import { IconButton } from '@mui/material';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { IDemandProposalViewModal } from 'viewModels';
import { toPattern } from 'vanilla-masker';
import { SolicitationController } from '../../../controller/SolicitationController';
import {
  AudienceType,
  HistoryType,
  labelAudienceType,
  labelServiceStatus,
  ServiceStatus,
} from '../../../types/enums';
import { Button } from '../../Buttons/Button';
import { CounterProposal } from '../../CounterProposal';
import { ModalRefuseProposal } from '../../Modals/ModalRefuseProposal';
import { Container, Header } from './styles';
import { ModalChat } from '../../Modals/ModalChat';
import { useApp, useAuth } from '../../../context';
import { useConversation } from '../../../context/conversation';
import { useAlert } from '../../../hooks/useAlert';
import { useSolicitation } from '../../../context/solicitation';
import { MASK_PROCESS_NUMBER, UF_NAMES_BY_CODE } from '../../../constants';
import { Attachment } from '../../Attachment';
import { useConfig } from '../../../context/configCustom';
import { ModalAlertConfirmation } from '../../Modals/ModalAlertConfirmation';

interface Props {
  demandProposal?: IDemandProposalViewModal;
  onRequestClose?: () => void;
  removeEvent?: () => void;
  isTutorial?: boolean;
}

export function CardDemandProposalDetails({
  demandProposal,
  onRequestClose,
  removeEvent,
  isTutorial = false,
}: Props) {
  const { uiVisibilitySettings } = useConfig();
  const app = useApp();
  const alert = useAlert();
  const { user } = useAuth();
  const { getConversationIdByParticipantsIds } = useConversation();
  const { setDemandProposals, setDemands } = useSolicitation();
  const [conversationIdSelected, setConversationIdSelected] = useState<string>();
  const [isAcceptingProposal, setIsAcceptingProposal] = useState(false);
  const [isOpenInformation, setIsOpenInformation] = useState(false);
  const [isOpenModalRefuseProposal, setIsOpenModalRefuseProposal] = useState(false);
  const [isOpenChat, setIsOpenChat] = useState(false);
  const [isOpenCounterProposal, setIsOpenCounterProposal] = useState(false);
  const [isOpenConfirmTermModal, setIsOpenConfirmTermModal] = useState(false);

  const handleAcceptProposal = () => {
    if (isTutorial) return;
    if (!demandProposal) return;
    if (uiVisibilitySettings?.showConfirmSolicitationTermModal) {
      setIsOpenConfirmTermModal(true);
    } else {
      requestAcceptProposal();
    }
  };

  const requestAcceptProposal = () => {
    if (isTutorial) return;
    if (!demandProposal) return;
    app.setLoading(true);
    setIsAcceptingProposal(true);
    SolicitationController.acceptProposal(demandProposal.solicitationId as string)
      .then((newDemand) => {
        setDemands((prev) => [newDemand, ...prev]);
        setDemandProposals((prev) => {
          const newDemandProposals = prev.filter((p) => p.id !== demandProposal?.id);
          return newDemandProposals;
        });
        onRequestClose?.();
        if (removeEvent) {
          removeEvent();
        }
        alert.show({ message: 'Proposta aceita com sucesso', type: 'success' });
      })
      .catch(() => {
        alert.show({ message: 'Erro ao aceitar proposta', type: 'error' });
      })
      .finally(() => {
        app.setLoading(false);
        setIsAcceptingProposal(false);
      });
  };

  const handleSelectConversation = () => {
    if (isTutorial) return;
    if (user.id && demandProposal?.userId) {
      app.setLoading(true);
      const participantsIds = [user.id, demandProposal.userId];
      getConversationIdByParticipantsIds(participantsIds)
        .then((conversationId) => {
          setConversationIdSelected(conversationId);
          setIsOpenChat(true);
        })
        .catch(() => {
          alert.show({ message: 'Não foi possível abrir o chat', type: 'error' });
        })
        .finally(() => {
          app.setLoading(false);
        });
    }
  };
  useEffect(() => {
    if (isTutorial) {
      setIsOpenInformation(true);
    }
  }, [isTutorial]);

  return (
    <Container status={demandProposal?.status!}>
      {demandProposal && (
        <>
          <Header status={demandProposal?.status!}>
            {!isOpenInformation && demandProposal.status !== ServiceStatus.CONCLUED ? (
              <div className="container-header-info">
                <div className="icon-title-status">
                  <StickyNote2Icon className="icon-header" />
                  <div className="containerGrid">
                    <div className="containerStatus">
                      {Object.keys(labelServiceStatus)
                        .filter((s) => Number(s) === Number(demandProposal.status))
                        .map((status) => (
                          <p className="statusCard" key={status + uuid()} id="demand-status">
                            Status: {labelServiceStatus[status as unknown as ServiceStatus]}
                          </p>
                        ))}
                      <p className="protocol" id="protocol-number">
                        Número de Protocolo: {demandProposal.protocolNumber}
                      </p>
                    </div>
                    <p className="title">{demandProposal.correspondence.name}</p>
                    <p className="createdAt">
                      Criada em:{' '}
                      <span>
                        {new Date(demandProposal.createdAt).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="service-date">
                  <p className="title">Prazo de Cumprimento</p>
                  <p>
                    {new Date(demandProposal.dateLimit).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            ) : (
              <div className="container-header">
                <SubtitlesIcon className="icon-header" />
                <div className="containerGrid">
                  <div className="containerStatus">
                    {Object.keys(labelServiceStatus)
                      .filter((s) => Number(s) === Number(demandProposal.status))
                      .map((status) => (
                        <p className="statusCard" key={status + uuid()} id="demand-status">
                          Status: {labelServiceStatus[status as unknown as ServiceStatus]}
                        </p>
                      ))}
                    <p id="protocol-number" className="protocol">
                      Número de Protocolo: {demandProposal.protocolNumber}
                    </p>
                  </div>
                  <p className="title">{demandProposal.correspondence.name}</p>
                  {demandProposal.status === ServiceStatus.CONCLUED && !isOpenInformation && (
                    <p className="createdAt">
                      Criada em:{' '}
                      <span>
                        {new Date(demandProposal.createdAt).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}{' '}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            )}
            <IconButton
              onClick={() => {
                setIsOpenInformation(!isOpenInformation);
              }}>
              {isOpenInformation ? <KeyboardDoubleArrowUpIcon /> : <KeyboardDoubleArrowDownIcon />}
            </IconButton>
          </Header>
          {isOpenInformation && (
            <div className="contentOpenInformation">
              <div className="containerData">
                <div className="containerInfos" id="demand-infos">
                  <div>
                    <div className="contentInfos">
                      <h1>Especialidade</h1>
                      <p>{demandProposal.specialty?.name ?? '-'}</p>
                    </div>
                    {demandProposal.processNumber && (
                      <div>
                        <h1>Nº do processo</h1>
                        <p>{toPattern(demandProposal.processNumber, MASK_PROCESS_NUMBER)}</p>
                      </div>
                    )}
                    {demandProposal.districtName && (
                      <div>
                        <h1>Comarca</h1>
                        <p>
                          {demandProposal.districtName} -{' '}
                          {UF_NAMES_BY_CODE[demandProposal.districtUfCode]}
                        </p>
                      </div>
                    )}
                    {demandProposal.partsNames?.length && (
                      <div>
                        <h1>Partes</h1>
                        {demandProposal.partsNames.map((part) => (
                          <p key={part.id}>{part.name}</p>
                        ))}
                      </div>
                    )}
                    {demandProposal.folderNumber && (
                      <div>
                        <h1>Nº da pasta</h1>
                        <p>{demandProposal.folderNumber}</p>
                      </div>
                    )}
                    {demandProposal?.address?.street && (
                      <div className="contentInfos">
                        <h1>Local</h1>
                        <p>
                          {demandProposal?.address?.street
                            ? `${demandProposal?.address?.street},`
                            : ''}
                          {demandProposal?.address?.number !== undefined &&
                          demandProposal?.address?.number
                            ? `${demandProposal?.address?.number},`
                            : 'S/Nº,'}
                          {demandProposal?.address?.city
                            ? `${demandProposal?.address?.city} - `
                            : ''}
                          {demandProposal?.address?.uf ?? ''}
                        </p>
                      </div>
                    )}
                    <div className="contentInfos">
                      <h1>Prazo de Cumprimento</h1>
                      <p>
                        {new Date(demandProposal.dateLimit).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    {demandProposal.note && (
                      <div className="contentInfos">
                        <h1>Observação</h1>
                        <p>{demandProposal.note}</p>
                      </div>
                    )}
                  </div>
                  <div>
                    {demandProposal.audienceType !== undefined && (
                      <div>
                        <h1>Tipo da Audiência</h1>
                        <p>
                          {
                            labelAudienceType[
                              demandProposal.audienceType as unknown as AudienceType
                            ]
                          }
                        </p>
                      </div>
                    )}
                    {demandProposal.client && (
                      <div>
                        <h1>Cliente</h1>
                        <p>{demandProposal?.client?.name}</p>
                      </div>
                    )}
                    {demandProposal.court && (
                      <div>
                        <h1>Vara</h1>
                        <p>{demandProposal.court?.name}</p>
                      </div>
                    )}
                    <div>
                      <h1>Detalhes</h1>
                      {demandProposal.isLawyer && demandProposal.isRepresentative ? (
                        <p>Advogado e Preposto</p>
                      ) : demandProposal.isLawyer ? (
                        <p>Advogado</p>
                      ) : (
                        <p>Preposto</p>
                      )}
                    </div>
                    {demandProposal.isVirtualAudience && demandProposal.audienceLink && (
                      <div>
                        <h1>Link da Audiência</h1>
                        <p>{demandProposal.audienceLink}</p>
                      </div>
                    )}
                    {demandProposal?.attachments?.length > 0 && (
                      <div className="containerAttachments">
                        <h1>Anexos</h1>
                        <div className="contentAttachments">
                          {demandProposal.attachments?.map((attachment) => (
                            <Attachment
                              key={attachment.id + uuid()}
                              data={{
                                id: attachment.id,
                                type: attachment.contentType ?? '',
                                name: attachment.name ?? 'Sem nome',
                              }}
                              options={{
                                disableDelete: true,
                              }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {demandProposal.history &&
                      demandProposal.history?.filter((h) => h.type === HistoryType.ATTACHMENT)
                        .length > 0 && (
                        <div className="containerAttachments">
                          <h1>Novos anexos</h1>
                          <div className="contentAttachments">
                            {demandProposal.history
                              ?.filter((h) => h.type === HistoryType.ATTACHMENT)
                              .map(({ attachments }) =>
                                attachments?.map((attachment) => (
                                  <Attachment
                                    key={attachment.id ?? attachment.url}
                                    data={{
                                      id: attachment.id,
                                      type: attachment.contentType ?? '',
                                      name: attachment.name ?? 'Sem nome',
                                      uri: attachment.url ?? '',
                                    }}
                                    options={{
                                      disableDelete: true,
                                    }}
                                  />
                                )),
                              )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
                <div className="containerCounterProposal">
                  <div>
                    <h1>Valor</h1>
                    <p>R$ {demandProposal.value}</p>
                  </div>
                  <div className="contentButton">
                    {!isOpenCounterProposal && demandProposal.createdById !== user.id ? (
                      <Button
                        id="counter-proposal"
                        text="Contra proposta"
                        dimensions={{ width: '80%', height: '2rem' }}
                        color="background_white"
                        backgroundHoverColor="primary"
                        background="secondary"
                        onClick={() => {
                          if (isTutorial) return;
                          setIsOpenCounterProposal(true);
                        }}
                      />
                    ) : (
                      <CounterProposal
                        isOpenCounterProposal={isOpenCounterProposal}
                        onRequestClose={() => {
                          if (isTutorial) return;
                          setIsOpenCounterProposal(false);
                        }}
                        demand={demandProposal}
                      />
                    )}
                  </div>
                  {!isOpenCounterProposal && demandProposal.createdById !== user.id && (
                    <div className="contentButton">
                      <Button
                        id="open-chat"
                        text="Abrir chat"
                        dimensions={{ width: '80%', height: '2rem' }}
                        color="background_white"
                        backgroundHoverColor="primary"
                        background="secondary"
                        onClick={handleSelectConversation}
                      />
                    </div>
                  )}
                </div>
              </div>
              {demandProposal.status === ServiceStatus.PENDING && (
                <div className="containerButons" id="accept-proposal">
                  <Button
                    text="Recusar"
                    dimensions={{ width: '12rem', height: '2rem' }}
                    color="tertiary_500"
                    backgroundHoverColor="tertiary_500"
                    border={{ color: 'tertiary_500', width: 1 }}
                    textHoverColor="background_white"
                    onClick={() => {
                      if (isTutorial) return;
                      setIsOpenModalRefuseProposal(true);
                    }}
                  />
                  <Button
                    text="Aceitar"
                    dimensions={{ width: '12rem', height: '2rem' }}
                    color="primary"
                    disabled={isAcceptingProposal}
                    backgroundHoverColor="primary"
                    textHoverColor="background_white"
                    border={{ color: 'primary', width: 1 }}
                    onClick={isAcceptingProposal ? () => {} : handleAcceptProposal}
                  />
                </div>
              )}
              <ModalRefuseProposal
                zIndex={101}
                isOpen={isOpenModalRefuseProposal}
                onRequestClose={() => {
                  if (isTutorial) return;
                  setIsOpenModalRefuseProposal(false);
                }}
                proposal={demandProposal}
              />
              <ModalChat
                zIndex={101}
                isOpen={isOpenChat}
                onRequestClose={() => {
                  if (isTutorial) return;
                  setIsOpenChat(false);
                }}
                conversationId={conversationIdSelected}
              />
            </div>
          )}
          {isOpenConfirmTermModal && (
            <ModalAlertConfirmation
              isOpen={isOpenConfirmTermModal}
              data={{
                title: 'Atenção',
                text: `Antes de continuar, esteja ciente de que a plataforma LegalHub atua apenas como intermediadora entre os usuários e os prestadores de serviço.
                        A LegalHub não se responsabiliza por eventuais problemas, fraudes ou falhas na execução dos serviços acordados entre as partes.
                        Recomendamos que você tenha atenção redobrada ao negociar valores ou condições.
                        Ao clicar em "Concordo", você declara estar ciente e de acordo com estas condições.`,
                type: 'warning',
                optionCancel: {
                  text: 'Cancelar',
                  active: true,
                },
                optionConfirm: {
                  text: 'Concordo',
                  active: true,
                },
              }}
              onRequestClose={() => setIsOpenConfirmTermModal(false)}
              onConfirm={() => requestAcceptProposal()}
            />
          )}
        </>
      )}
    </Container>
  );
}
